// src/components/TournamentModal.tsx

import React, { useEffect, useCallback, useState } from 'react';
import {
  TournamentState,
  TournamentRecord,
} from '../utils/bucketingState';
import {
  recordMatchResult,
  undoMatchResult,
  isRoundComplete,
  isTournamentComplete,
  advanceToNextRound,
  getCurrentMatch,
  getFinalRankings,
  getTournamentProgress,
} from '../utils/tournamentRanking';
import './TournamentModal.css';

interface TaskForTournament {
  id: string;
  title: string;
  categoryName: string;
  description?: string;
}

interface TournamentModalProps {
  opened: boolean;
  onClose: () => void;
  state: TournamentState;
  tasks: TaskForTournament[];
  onStateChange: (state: TournamentState) => void;
  onComplete: (rankings: Array<{ taskId: string; rank: number }>) => void;
  onMarkTaskDone: (taskId: string) => void;
}

function formatRecord(record: TournamentRecord): string {
  const total = record.wins + record.losses + record.ties;
  if (total === 0) return '0-0';
  if (record.ties > 0) {
    return `${record.wins}-${record.losses}-${record.ties}`;
  }
  return `${record.wins}-${record.losses}`;
}

export const TournamentModal: React.FC<TournamentModalProps> = ({
  opened,
  onClose,
  state,
  tasks,
  onStateChange,
  onComplete,
  onMarkTaskDone,
}) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });

  const isComplete = isTournamentComplete(state);
  const currentMatch = getCurrentMatch(state);
  const progress = getTournamentProgress(state);
  const canUndo = state.history.length > 0;

  const taskA = currentMatch ? tasks.find(t => t.id === currentMatch.taskA) : null;
  const taskB = currentMatch ? tasks.find(t => t.id === currentMatch.taskB) : null;

  const hasDescriptionA = taskA?.description && taskA.description.trim().length > 0;
  const hasDescriptionB = taskB?.description && taskB.description.trim().length > 0;

  const handleChoice = useCallback((winner: string | 'skip') => {
    let newState = recordMatchResult(state, winner);

    // Auto-advance to next round if current round is complete
    if (isRoundComplete(newState) && !isTournamentComplete(newState)) {
      newState = advanceToNextRound(newState);
    }

    // Reset expanded descriptions for next match
    setExpandedDescriptions({ left: false, right: false });

    onStateChange(newState);
  }, [state, onStateChange]);

  const handleUndo = useCallback(() => {
    const newState = undoMatchResult(state);
    onStateChange(newState);
  }, [state, onStateChange]);

  const handleComplete = useCallback(() => {
    const rankings = getFinalRankings(state);
    onComplete(rankings.map(r => ({ taskId: r.taskId, rank: r.rank })));
    onClose();
  }, [state, onComplete, onClose]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!opened || isComplete) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (!currentMatch) return;

      switch (e.key) {
        case 'ArrowLeft':
          handleChoice(currentMatch.taskA);
          break;
        case 'ArrowRight':
          handleChoice(currentMatch.taskB);
          break;
        case 'ArrowUp':
          handleChoice('skip');
          break;
        case 'ArrowDown':
          if (canUndo) handleUndo();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [opened, isComplete, currentMatch, handleChoice, handleUndo, canUndo, onClose]);

  if (!opened) return null;

  // Results screen
  if (isComplete) {
    const rankings = getFinalRankings(state);

    return (
      <div className="tournament-modal-overlay" onClick={onClose}>
        <div className="tournament-modal-content tournament-results-container" onClick={e => e.stopPropagation()}>
          <div className="tournament-results-title">Final Rankings</div>
          <div className="tournament-results-scrollable">
            <div className="tournament-results-list">
              {rankings.map(({ taskId, record, rank }) => {
                const task = tasks.find(t => t.id === taskId);
                return (
                  <div key={taskId} className="tournament-result-row">
                    <div className={`tournament-result-rank ${rank <= 3 ? 'top' : 'normal'}`}>
                      #{rank}
                    </div>
                    <div className="tournament-result-title">
                      {task?.title || 'Unknown task'}
                    </div>
                    <div className="tournament-result-record">
                      {formatRecord(record)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="tournament-results-footer">
            <button className="tournament-close-button" onClick={handleComplete}>
              Apply Rankings
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tournament match UI
  // Calculate progress percentages
  const roundProgress = progress.matchesInRound > 0
    ? ((progress.matchInRound - 1) / progress.matchesInRound) * 100
    : 0;
  const overallTotal = progress.completedMatches + progress.estimatedRemaining;
  const overallProgress = overallTotal > 0
    ? (progress.completedMatches / overallTotal) * 100
    : 0;

  return (
    <div className="tournament-modal-overlay" onClick={onClose}>
      <div className="tournament-modal-content" onClick={e => e.stopPropagation()}>
        {/* Overall progress bar */}
        <div className="tournament-overall-progress">
          <div
            className="tournament-overall-progress-fill"
            style={{ width: `${overallProgress}%` }}
          />
        </div>

        <div className="tournament-rounds">
          {Array.from({ length: progress.totalRounds }, (_, i) => {
            const roundNum = i + 1;
            const isCompleted = roundNum < state.currentRound;
            const isCurrent = roundNum === state.currentRound;
            let className = 'tournament-round-badge ';
            if (isCompleted) {
              className += 'completed';
            } else if (isCurrent) {
              className += 'current';
            } else {
              className += 'upcoming';
            }
            return (
              <span key={roundNum} className={className}>
                Round {roundNum} {isCompleted && '✓'}
                {isCurrent && (
                  <span
                    className="tournament-round-progress"
                    style={{ width: `${roundProgress}%` }}
                  />
                )}
              </span>
            );
          })}
        </div>

        <div className="tournament-match-info">
          <div className="tournament-match-primary">
            Round {progress.currentRound}: {progress.matchInRound} of {progress.matchesInRound} · Overall: {progress.completedMatches} done, ~{progress.estimatedRemaining} left
          </div>
          <div className="tournament-match-question">Which is more important?</div>
        </div>

        {taskA && taskB && (
          <div className="tournament-match-cards">
            <div className="tournament-card-wrapper">
              <div
                className="tournament-card left"
                onClick={() => handleChoice(currentMatch!.taskA)}
              >
                <div className="tournament-card-record">
                  {formatRecord(state.results[taskA.id])}
                </div>
                <div className="tournament-card-title">{taskA.title}</div>
                <div className="tournament-card-category">{taskA.categoryName}</div>
                {hasDescriptionA && (
                  <div className="tournament-description-section">
                    <button
                      className="tournament-description-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDescriptions(prev => ({ ...prev, left: !prev.left }));
                      }}
                    >
                      <span className={`tournament-description-arrow ${expandedDescriptions.left ? 'expanded' : ''}`}>▶</span>
                      {expandedDescriptions.left ? 'Hide' : 'Details'}
                    </button>
                    {expandedDescriptions.left && (
                      <div
                        className="tournament-task-description"
                        onClick={(e) => e.stopPropagation()}
                        dangerouslySetInnerHTML={{ __html: taskA.description || '' }}
                      />
                    )}
                  </div>
                )}
              </div>
              <button
                className="tournament-mark-done-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkTaskDone(taskA.id);
                }}
              >
                ✓ Done
              </button>
            </div>

            <div className="tournament-vs">vs</div>

            <div className="tournament-card-wrapper">
              <div
                className="tournament-card right"
                onClick={() => handleChoice(currentMatch!.taskB)}
              >
                <div className="tournament-card-record">
                  {formatRecord(state.results[taskB.id])}
                </div>
                <div className="tournament-card-title">{taskB.title}</div>
                <div className="tournament-card-category">{taskB.categoryName}</div>
                {hasDescriptionB && (
                  <div className="tournament-description-section">
                    <button
                      className="tournament-description-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDescriptions(prev => ({ ...prev, right: !prev.right }));
                      }}
                    >
                      <span className={`tournament-description-arrow ${expandedDescriptions.right ? 'expanded' : ''}`}>▶</span>
                      {expandedDescriptions.right ? 'Hide' : 'Details'}
                    </button>
                    {expandedDescriptions.right && (
                      <div
                        className="tournament-task-description"
                        onClick={(e) => e.stopPropagation()}
                        dangerouslySetInnerHTML={{ __html: taskB.description || '' }}
                      />
                    )}
                  </div>
                )}
              </div>
              <button
                className="tournament-mark-done-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkTaskDone(taskB.id);
                }}
              >
                ✓ Done
              </button>
            </div>
          </div>
        )}

        <div className="tournament-keyboard-hint">
          ← Left wins | → Right wins | ↑ Skip (tie) | ↓ Undo
        </div>

        <div className="tournament-actions">
          <button
            className="tournament-action-button"
            onClick={handleUndo}
            disabled={!canUndo}
          >
            ↩ Undo
          </button>
          <button
            className="tournament-action-button"
            onClick={() => handleChoice('skip')}
          >
            Skip (Tie)
          </button>
          <button
            className="tournament-action-button"
            onClick={onClose}
          >
            Save & Exit
          </button>
        </div>
      </div>
    </div>
  );
};
